import CorporateService from '../services/corporate.service.js';
import PostalCodeService from '../services/postal.code.service.js';
import { InventoryService } from '../services/orphan.service';

function filter(array, fn) {
  const results = [];
  let item;
  for (let i = 0, len = array.length; i < len; i++) {
    item = array[i];
    if (fn(item)) results.push(item);
  }
  return results;
}

const initialState = {
  _lanes: null,
  _loadingLocations: false,
  _postalCodes: null,
  _postalCodePrefixes: [],
  _postalCodeLaneAvailablePrefixes: [],
  _terminals: [],
  _terminalTypes: [{ name: 'Agent' }, { name: 'Terminal' }],
  _statuses: [
    { name: 'Created' },
    { name: 'Planning' },
    { name: 'Arrived' },
    { name: 'Released' },
    { name: 'Closed' },
    { name: 'ClosedInbound' },
    { name: 'Loading' },
    { name: 'Loaded' },
    { name: 'Unloading' },
    { name: 'Unloaded' },
    { name: 'InTransit' },
  ],

  _locationTypes: [{ name: 'Floor' }, { name: 'Door' }, { name: 'Manifest' }],

  _proStatuses: [
    { name: 'Created' },
    { name: 'PickupScheduled' },
    { name: 'PickupAssigned' },
    { name: 'Arrived' },
    { name: 'InProcess' },
    { name: 'InTransit' },
    { name: 'Stowed' },
    { name: 'Delivered' },
  ],
  _doors: null,
  _locations: null,
  _bay_inventory: null,
  _terminal_inventory: null,
  _activeLocations: [],
  _zones: null,
  _activeZone: null,
  _activeLane: null,
};

export const corporate = {
  namespaced: true,
  state: initialState,
  getters: {
    lanes: (state) => state._lanes,
    activeLane: (state) => state._activeLane,
    postalCodeLaneAvailablePrefixes: (state) =>
      state._postalCodeLaneAvailablePrefixes,
    postalCodes: (state) => state._postalCodes,
    postalCodePrefixes: (state) => state._postalCodePrefixes,
    client: (state, getters, rootState) => {
      return new CorporateService(rootState.auth.user.token);
    },
    postal_code_client: (state, getters, rootState) => {
      return new PostalCodeService(rootState.auth.user.token);
    },
    inventory_client: (state, getters, rootState) => {
      return InventoryService.getInstance(rootState.auth.user.token);
    },
    terminals: (state) => state._terminals,
    hubs: (state) => state._terminals.filter((t) => t.type == 'Terminal'),
    agents: (state) => state._terminals.filter((t) => t.type == 'Agent'),
    doors: (state) => state._doors,
    locations: (state) => state._locations,
    bayInventory: (state) => state._bay_inventory,
    terminalInventory: (state) => state._terminal_inventory,
    // Sorted alphanumerically by barcode. The API returns these in insertion order,
    // which put location pickers in no discernible order and forced operators to
    // scroll the whole list hunting for a bay. `numeric: true` keeps runs like
    // 01AB0502 / 01AB0511 in human order instead of lexical (2 before 11).
    activeLocations: (state) =>
      [...(state._activeLocations ?? [])].sort((a, b) =>
        (a?.barcode ?? "").localeCompare(b?.barcode ?? "", undefined, {
          numeric: true,
          sensitivity: "base",
        })
      ),
    loadingLocations: (state) => state._loadingLocations,
    zones: (state) => state._zones,
    activeZone: (state) => state._activeZone,
    terminalDoors: (state) => (code) => {
      if (state._doors == null || state._terminals.length == 0) return [];
      const terminal = state._terminals.filter((t) => t.code == code)[0];
      return state._doors.filter((door) => door.terminal == terminal.number);
    },
    getLocationsByTerminal: (state) => (terminalNumber) => {
      if (state._locations == null || state._locations.length == 0) return [];
      return filter(state._locations, function (x) {
        return x.terminal == terminalNumber;
      });
    },
    statuses: (state) => state._statuses,
    proStatuses: (state) => state._proStatuses,
    locationTypes: (state) => state._locationTypes,
    terminalTypes: (state) => state._terminalTypes,
  },
  actions: {
    async getLanes({ commit, getters, dispatch }) {
      const client = getters.client;
      return client
        .getLanesList()
        .then((data) => {
          commit('SET_LANES', data.result);
        })
        .catch((error) => {
          if (error.isError && error.status == 401) {
            dispatch('auth/logout', {}, { root: true });
          }
        });
    },

    async getLane({ commit, getters, dispatch }, params) {
      const client = getters.client;
      return client
        .getLane(params.code)
        .then((data) => {
          commit('SET_ACTIVE_LANE', data.result);
        })
        .catch((error) => {
          if (error.isError && error.status == 401) {
            dispatch('auth/logout', {}, { root: true });
          }
        });
    },
    addLane({ getters, dispatch }, params) {
      const client = getters.client;
      console.log('params: ', params);
      return client
        .addNewLane(params)
        .then(() => { })
        .catch((error) => {
          if (error.isError && error.status == 401) {
            dispatch('auth/logout', {}, { root: true });
          }
          return error;
        });
    },
    removeLane({ getters, dispatch }, params) {
      const client = getters.client;
      return client
        .removeLane(params.barcode)
        .then(() => { })
        .catch((error) => {
          if (error.isError && error.status == 401) {
            dispatch('auth/logout', {}, { root: true });
          }
          return error;
        });
    },
    getDoors({ commit, getters, dispatch }) {
      const client = getters.client;
      return client
        .getDoorsList()
        .then((data) => {
          console.log('data', data);
          commit('SET_DOORS', data.result);
        })
        .catch((error) => {
          if (error.isError && error.status == 401) {
            dispatch('auth/logout', {}, { root: true });
          }
        });
    },
    addDoor({ getters, dispatch }, params) {
      const client = getters.client;
      return client
        .addDoor(params)
        .then(() => {
          dispatch('getDoors', {});
        })
        .catch((error) => {
          if (error.isError && error.status == 401) {
            dispatch('auth/logout', {}, { root: true });
          }
          return error;
        });
    },
    getLocations({ commit, getters, dispatch }) {
      const client = getters.client;
      return client
        .getLocations()
        .then((data) => {
          commit('SET_LOCATIONS', data.result);
        })
        .catch((error) => {
          if (error.isError && error.status == 401) {
            dispatch('auth/logout', {}, { root: true });
          }
        });
    },
    getInventoryByTerminal({ commit, getters, dispatch }, params) {
      const client = getters.client;
      return client
        .getInventoryByTerminal(params.terminal)
        .then((data) => {
          commit('SET_TERMINAL_INVENTORY', data.result);
        })
        .catch((error) => {
          if (error.isError && error.status == 401) {
            dispatch('auth/logout', {}, { root: true });
          }
        });
    },
    exportInventoryForTerminal({ getters, dispatch }, params) {
      const client = getters.client;
      return client
        .exportInventoryForTerminal(params.terminal)
        .then((response) => {
          const headers = response.headers;
          const fileName = response.fileName ? response.fileName : 'export.csv';
          const blob = new Blob([response.data], {
            type: headers['content-type'],
          });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = fileName;
          link.click();
        })
        .catch((error) => {
          console.log(error);
          if (error.isError && error.status == 401) {
            dispatch('auth/logout', {}, { root: true });
          }
        });
    },

    getInventoryForBay({ commit, getters, dispatch }, params) {
      const client = getters.client;
      return client
        .getInventoryForBay(params.terminal, params.row, params.bay)
        .then((data) => {
          commit('SET_BAY_INVENTORY', data.result);
        })
        .catch((error) => {
          if (error.isError && error.status == 401) {
            dispatch('auth/logout', {}, { root: true });
          }
        });
    },
    validateInventory({ getters, dispatch }, params) {
      const client = getters.client;
      console.log('params: ', params);
      return client
        .validateInventory(params.barcode, { barcode: params.barcode, terminal: params.terminal, location: params.location })
        .then(() => {
          dispatch(
            'toast/success',
            `${params.barcode} validated at terminal ${params.terminal} location ${params.location}`,
            {
              root: true,
            }
          );
        })
        .catch((error) => {
          if (error.isError && error.status == 401) {
            dispatch('auth/logout', {}, { root: true });
          }
          dispatch('toast/error', `${params.barcode} validation failed`, {
            root: true,
          });
          return error;
        });
    },
    async getLocationsForTerminal({ commit, getters, dispatch }, params) {
      const client = getters.client;
      console.log('here', params);
      return client
        .getLocationsByTerminalList(params.terminal)
        .then((data) => {
          console.log(data);
          commit('SET_ACTIVE_LOCATIONS', data.result);
        })
        .catch((error) => {
          if (error.isError && error.status == 401) {
            dispatch('auth/logout', {}, { root: true });
          }
        });
    },

    async locationsByTerminalListGrouped(
      { commit, getters, dispatch },
      params
    ) {
      const client = getters.client;
      return client
        .locationsByTerminalListGrouped(params.terminal)
        .then((data) => {
          console.log(data);
          commit('SET_ACTIVE_LOCATIONS', data.result);
        })
        .catch((error) => {
          if (error.isError && error.status == 401) {
            dispatch('auth/logout', {}, { root: true });
          }
        });
    },

    addLocation({ getters, dispatch }, params) {
      const client = getters.client;
      console.log('params: ', params);
      return client
        .addLocation(params)
        .then(() => { })
        .catch((error) => {
          if (error.isError && error.status == 401) {
            dispatch('auth/logout', {}, { root: true });
          }
          return error;
        });
    },

    removeLocation({ getters, dispatch }, params) {
      const client = getters.client;
      console.log('params: ', params);
      return client
        .removeLocation(params.barcode)
        .then(() => { })
        .catch((error) => {
          if (error.isError && error.status == 401) {
            dispatch('auth/logout', {}, { root: true });
          }
          return error;
        });
    },

    getTerminals({ commit, getters, dispatch }) {
      const client = getters.client;
      return client
        .getHubTerminalsList()
        .then((data) => {
          commit('SET_TERMINALS', data.result);
          // Cache the result
          localStorage.setItem('corporateTerminals', JSON.stringify(data.result));
        })
        .catch((error) => {
          if (error.isError && error.status == 401) {
            dispatch('auth/logout', {}, { root: true });
          }
        });
    },

    getPostalCodePrefixes({ commit, getters, dispatch }) {
      const client = getters.client;
      return client
        .getPostalCodePrefixes()
        .then((data) => {
          commit('SET_POSTAL_CODE_PREFIXES', data.result.prefixes);
        })
        .catch((error) => {
          if (error.isError && error.status == 401) {
            dispatch('auth/logout', {}, { root: true });
          }
        });
    },

    getPostalCodeLaneAvailablePrefixes({ commit, getters, dispatch }) {
      const client = getters.client;
      return client
        .getPostalCodeLaneAvailablePrefixes()
        .then((data) => {
          commit(
            'SET_POSTAL_CODE_LANE_AVAILABLE_PREFIXES',
            data.result.prefixes
          );
        })
        .catch((error) => {
          if (error.isError && error.status == 401) {
            dispatch('auth/logout', {}, { root: true });
          }
        });
    },

    addTerminal({ getters, dispatch }, params) {
      const client = getters.client;
      return client
        .addNewHubTerminal({ ...params })
        .then(() => {
          dispatch('getTerminals', {});
        })
        .catch((error) => {
          if (error.isError && error.status == 401) {
            dispatch('auth/logout', {}, { root: true });
          }
          return error;
        });
    },

    async getPostalCodes({ commit, getters, dispatch }, params) {
      const client = getters.client;
      return client
        .getPostalCodes(params)
        .then((data) => {
          commit('SET_POSTAL_CODES', data.result);
        })
        .catch((error) => {
          if (error.isError && error.status == 401) {
            dispatch('auth/logout', {}, { root: true });
          }
        });
    },
    async addNewZone({ getters, dispatch }, params) {
      const client = getters.client;
      return client.addNewZone(params).catch((error) => {
        if (error.isError && error.status == 401) {
          dispatch('auth/logout', {}, { root: true });
        }
      });
    },
    async getZones({ commit, getters, dispatch }) {
      const client = getters.client;
      return client
        .getZonesList()
        .then((data) => {
          commit('SET_ZONES', data.result);
        })
        .catch((error) => {
          if (error.isError && error.status == 401) {
            dispatch('auth/logout', {}, { root: true });
          }
        });
    },
    async getZone({ commit, getters, dispatch }, params) {
      const client = getters.client;
      return client
        .getZone(params.code)
        .then((data) => {
          commit('SET_ACTIVE_ZONE', data.result);
        })
        .catch((error) => {
          if (error.isError && error.status == 401) {
            dispatch('auth/logout', {}, { root: true });
          }
        });
    },
  },
  mutations: {
    SET_DOORS(state, data) {
      state._doors = data;
    },
    SET_LOCATIONS(state, data) {
      state._locations = data;
    },
    SET_BAY_INVENTORY(state, data) {
      state._bay_inventory = data;
    },
    SET_TERMINAL_INVENTORY(state, data) {
      state._terminal_inventory = data.inventoryItems;
    },
    SET_ACTIVE_LOCATIONS(state, data) {
      state._activeLocations = data;
    },
    SET_TERMINALS(state, data) {
      state._terminals = data;
    },
    SET_LANES(state, data) {
      state._lanes = data;
    },
    SET_POSTAL_CODE_LANE_AVAILABLE_PREFIXES(state, data) {
      state._postalCodeLaneAvailablePrefixes = data;
    },
    SET_ACTIVE_LANE(state, data) {
      state._activeLane = data;
    },
    SET_POSTAL_CODES(state, data) {
      state._postalCodes = data;
    },
    SET_POSTAL_CODE_PREFIXES(state, data) {
      state._postalCodePrefixes = data;
    },
    SET_ZONES(state, data) {
      state._zones = data;
    },
    SET_ACTIVE_ZONE(state, data) {
      state._activeZone = data;
    },
  },
};
